import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Sparkles, Wrench } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = [
    { key: "pending", label: "Pending", color: "bg-red-50 border-red-200", dotColor: "bg-red-500" },
    { key: "in_progress", label: "In Progress", color: "bg-yellow-50 border-yellow-200", dotColor: "bg-yellow-500" },
    { key: "inspecting", label: "Inspecting", color: "bg-blue-50 border-blue-200", dotColor: "bg-blue-500" },
    { key: "clean", label: "Clean & Ready", color: "bg-emerald-50 border-emerald-200", dotColor: "bg-emerald-500" },
];

export default function Housekeeping() {
    const { activeHotelId } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ room_id: "", task_type: "cleaning", priority: "normal", notes: "" });

    const load = async () => {
        if (!activeHotelId) return;
        const [t, r] = await Promise.all([
            api.get(`/housekeeping/tasks?hotel_id=${activeHotelId}`),
            api.get(`/rooms?hotel_id=${activeHotelId}`),
        ]);
        setTasks(t.data);
        setRooms(r.data);
    };

    useEffect(() => { load(); }, [activeHotelId]);

    const createTask = async () => {
        const room = rooms.find(r => r.id === form.room_id);
        if (!room) return;
        try {
            await api.post("/housekeeping/tasks", {
                ...form, hotel_id: activeHotelId, room_number: room.number,
            });
            toast.success("Task created");
            setOpen(false); setForm({ room_id: "", task_type: "cleaning", priority: "normal", notes: "" });
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const moveTask = async (task, status) => {
        try {
            await api.patch(`/housekeeping/tasks/${task.id}`, { status });
            toast.success(`Task moved to ${status}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Auto-generate tasks for dirty rooms
    const generateForDirty = async () => {
        const dirty = rooms.filter(r => r.status === "dirty");
        if (dirty.length === 0) { toast.info("No dirty rooms to clean"); return; }
        for (const r of dirty) {
            if (tasks.some(t => t.room_id === r.id && t.status !== "clean")) continue;
            await api.post("/housekeeping/tasks", {
                hotel_id: activeHotelId, room_id: r.id, room_number: r.number,
                task_type: "cleaning", priority: "normal", notes: "",
            });
        }
        toast.success("Tasks generated for dirty rooms");
        load();
    };

    return (
        <AppShell title="Housekeeping" subtitle={`${tasks.length} tasks · ${rooms.filter(r => r.status === "dirty").length} rooms need cleaning`}>
            <div className="flex justify-end gap-2 mb-4">
                <Button variant="outline" onClick={generateForDirty} className="rounded-sm" data-testid="hk-generate-btn">
                    <Sparkles className="w-4 h-4 mr-1" /> Auto-generate
                </Button>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button className="bg-[#0A1C3E] rounded-sm" data-testid="hk-add-btn"><Plus className="w-4 h-4 mr-1" /> New Task</Button></DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>New Housekeeping Task</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div><Label className="text-xs uppercase tracking-wider">Room</Label>
                                <Select value={form.room_id} onValueChange={v => setForm({ ...form, room_id: v })}>
                                    <SelectTrigger><SelectValue placeholder="Select room" /></SelectTrigger>
                                    <SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.id}>Room {r.number} · {r.status}</SelectItem>)}</SelectContent>
                                </Select></div>
                            <div><Label className="text-xs uppercase tracking-wider">Task Type</Label>
                                <Select value={form.task_type} onValueChange={v => setForm({ ...form, task_type: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="cleaning">Cleaning</SelectItem>
                                        <SelectItem value="deep_cleaning">Deep Cleaning</SelectItem>
                                        <SelectItem value="maintenance">Maintenance</SelectItem>
                                        <SelectItem value="inspection">Inspection</SelectItem>
                                    </SelectContent>
                                </Select></div>
                            <div><Label className="text-xs uppercase tracking-wider">Priority</Label>
                                <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low">Low</SelectItem>
                                        <SelectItem value="normal">Normal</SelectItem>
                                        <SelectItem value="high">High</SelectItem>
                                    </SelectContent>
                                </Select></div>
                            <div><Label className="text-xs uppercase tracking-wider">Notes</Label>
                                <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
                        </div>
                        <DialogFooter><Button onClick={createTask} disabled={!form.room_id} className="bg-[#0A1C3E]" data-testid="hk-submit">Create</Button></DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {COLUMNS.map(col => {
                    const list = tasks.filter(t => t.status === col.key);
                    return (
                        <div key={col.key} className={`p-3 border rounded-md ${col.color}`} data-testid={`hk-col-${col.key}`}>
                            <div className="flex justify-between items-center mb-3 px-1">
                                <div className="flex items-center gap-2">
                                    <span className={`w-2 h-2 rounded-full ${col.dotColor}`} />
                                    <span className="text-xs font-bold uppercase tracking-wider text-slate-700">{col.label}</span>
                                </div>
                                <span className="text-xs font-mono text-slate-600 bg-white px-2 py-0.5 rounded-full">{list.length}</span>
                            </div>
                            <div className="space-y-2">
                                {list.map(t => {
                                    const nextIdx = COLUMNS.findIndex(c => c.key === col.key) + 1;
                                    const next = COLUMNS[nextIdx];
                                    return (
                                        <Card key={t.id} className="p-3 bg-white border border-slate-200 shadow-none rounded" data-testid={`hk-task-${t.id}`}>
                                            <div className="flex justify-between items-start">
                                                <div className="font-mono font-bold text-sm">Room {t.room_number}</div>
                                                {t.priority === "high" && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-semibold">HIGH</span>}
                                            </div>
                                            <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                                                {t.task_type === "maintenance" ? <Wrench className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
                                                {t.task_type?.replace(/_/g, " ")}
                                            </div>
                                            {t.notes && <div className="text-xs text-slate-600 mt-1">{t.notes}</div>}
                                            {next && (
                                                <button onClick={() => moveTask(t, next.key)} className="mt-2 w-full text-xs bg-slate-100 hover:bg-[#0A1C3E] hover:text-white py-1 rounded transition-colors" data-testid={`hk-move-${t.id}`}>
                                                    → {next.label}
                                                </button>
                                            )}
                                        </Card>
                                    );
                                })}
                                {list.length === 0 && <div className="text-xs text-slate-500 text-center py-6">Empty</div>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </AppShell>
    );
}
